import React from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss, Flex, Text } from '@atlaskit/primitives';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';
import type { FormatOptionLabelMeta } from '@atlaskit/select';
import Select from '@atlaskit/select';

import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';

const i18n = defineMessages({
	archivedSpacesTooltip: {
		id: 'bulk-permissions.space.archived-spaces-tooltip',
		defaultMessage: 'Archived spaces are not included in bulk permissions actions.',
		description: 'Tooltip text for the space selection section for the bulk access page',
	},
	archivedSpacesTooltipBulkAccess: {
		id: 'bulk-permissions.space.archived-spaces-tooltip-bulk-access',
		defaultMessage: 'Archived spaces are not included in bulk access actions.',
		description: 'Tooltip text for the space selection section for the bulk access page',
	},
	archivedSpacesTooltipIconLabel: {
		id: 'bulk-permissions.space.archived-spaces-tooltip-icon-label',
		defaultMessage: 'Archived space information',
		description: 'Label for the archived spaces tooltip icon',
	},
	spaceSelectionHeader: {
		id: 'bulk-permissions.space.space-selection-header',
		defaultMessage: 'Choose which spaces',
		description: 'Header text of the space selection section',
	},
	spaceAccessSelectionHeader: {
		id: 'bulk-permissions.space.space-access-selection-header',
		defaultMessage: 'On which spaces<asterisk>*</asterisk>',
		description: 'Header text of the space selection section for the bulk access page',
	},
	spaceAccessInclusionExclusionSelectionHeader: {
		id: 'bulk-permissions.space.space-access-inclusion-exclusion-selection-header',
		defaultMessage: 'For which spaces<asterisk>*</asterisk>',
		description: 'Header text of the space selection section for the bulk access page',
	},
	spaceMenuTrigger: {
		id: 'bulk-permissions.space.space-access-menu-trigger.non-final',
		defaultMessage: 'Select spaces',
		description: 'Placeholder for the dropdown menu to select what type of spaces to bulk audit',
	},
	spaceMenuNoOptions: {
		id: 'bulk-permissions.space.space-access-menu-no-options.non-final',
		defaultMessage: 'No spaces match your search',
		description: 'Placeholder for the spaces table to select what type of spaces to bulk audit',
	},
	spaceSelectionOne: {
		id: 'bulk-permissions.space.space-selection-one',
		defaultMessage: 'All',
		description: 'Option to select all Confluence spaces including personal spaces',
	},
	spaceSelectionOneDescription: {
		id: 'bulk-permissions.space.space-selection-one.description.non-final',
		defaultMessage: 'All spaces within your tenant',
		description:
			'Description for the option to select all Confluence spaces including personal spaces',
	},
	spaceSelectionTwo: {
		id: 'bulk-permissions.space.space-selection-two',
		defaultMessage: 'All (excluding personal)',
		description: 'Option to select all Confluence spaces except personal spaces',
	},
	spaceSelectionTwoDescription: {
		id: 'bulk-permissions.space.space-selection-two.description.non-final',
		defaultMessage: 'All spaces within your tenant, except for personal spaces.',
		description:
			'Description for the option to select all Confluence spaces excluding personal spaces',
	},
	spaceSelectionThree: {
		id: 'bulk-permissions.space.space-selection-three.non-final',
		defaultMessage: 'All (excluding specific spaces)',
		description: 'Option to select personal Confluence spaces',
	},
	spaceSelectionThreeDescription: {
		id: 'bulk-permissions.space.space-selection-three.description.non-final',
		defaultMessage: 'All spaces within your tenant, excluding selected specific spaces',
		description: 'Description for the option to select personal spaces',
	},
	spaceSelectionFour: {
		id: 'bulk-permissions.space.space-selection-four',
		defaultMessage: 'Only personal',
		description: 'Option to select personal Confluence spaces',
	},
	spaceSelectionFourDescription: {
		id: 'bulk-permissions.space.space-selection-four.description.non-final',
		defaultMessage: 'Only select personal spaces within this tenant',
		description: 'Description for the option to select personal spaces',
	},
	spaceSelectionFive: {
		id: 'bulk-permissions.space.space-selection-five.non-final',
		defaultMessage: 'Only specific spaces',
		description: 'Option to select personal Confluence spaces',
	},
	spaceSelectionFiveDescription: {
		id: 'bulk-permissions.space.space-selection-five.description.non-final',
		defaultMessage: 'Only select specific spaces',
		description: 'Description for the option to select specific spaces',
	},
});

export enum SpaceSelection {
	AllExcludingPersonal = 'all-excluding-personal',
	AllIncludingPersonal = 'all-including-personal',
	OnlyPersonal = 'only-personal',
	//the following are options for GA
	OnlySpecificSpaces = 'only-specific-spaces',
	AllExcludingSpecific = 'all-excluding-specific',
}

type SpaceSelectionOptionType = {
	value: SpaceSelection;
	label: MessageDescriptor;
	description: MessageDescriptor;
};

const spaceSelectionOptions: SpaceSelectionOptionType[] = [
	{
		value: SpaceSelection.AllExcludingPersonal,
		label: i18n.spaceSelectionOne,
		description: i18n.spaceSelectionOneDescription,
	},
	{
		value: SpaceSelection.AllIncludingPersonal,
		label: i18n.spaceSelectionTwo,
		description: i18n.spaceSelectionTwoDescription,
	},
	{
		value: SpaceSelection.AllExcludingSpecific,
		label: i18n.spaceSelectionThree,
		description: i18n.spaceSelectionThreeDescription,
	},
	{
		value: SpaceSelection.OnlyPersonal,
		label: i18n.spaceSelectionFour,
		description: i18n.spaceSelectionFourDescription,
	},
	{
		value: SpaceSelection.OnlySpecificSpaces,
		label: i18n.spaceSelectionFive,
		description: i18n.spaceSelectionFiveDescription,
	},
];

const spaceSelectionHeaderStyle = xcss({
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	display: 'flex',
	flexDirection: 'row',
	paddingTop: 'space.300',
	paddingBottom: 'space.100',
});

const spaceSelectionRadioButtonStyle = xcss({
	marginBottom: 'space.300',
});

const spaceSelectionSelectStyle = xcss({
	width: '300px',
	paddingBottom: 'space.100',
});

const asteriskStyle = xcss({
	color: 'color.text.accent.red',
});

const optionStyles = xcss({
	paddingTop: 'space.050',
	paddingBottom: 'space.050',
	flexDirection: 'column',
});

type SpaceSelectionRadioButtonProps = {
	spaceSelection: SpaceSelection;
	onRadioChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onMenuOpen?: () => void;
	onChange?: (value: SpaceSelection) => void;
};

export const SpaceSelectionRadioButtons = ({
	spaceSelection,
	onRadioChange,
	onMenuOpen,
	onChange,
}: SpaceSelectionRadioButtonProps) => {
	const { formatMessage } = useIntl();
	const { isRbacEnabled } = useSpaceRoleMode();

	const getOptionLabel = (option: SpaceSelectionOptionType) => {
		return `${option.label} ${option.description}`;
	};

	const formatOptionLabel = (
		option: SpaceSelectionOptionType,
		{ selectValue }: FormatOptionLabelMeta<SpaceSelectionOptionType>,
	) => {
		const selected = selectValue?.[0]?.value;
		const isSelected = selected ? selected === option.value : false;
		return (
			<Flex xcss={optionStyles}>
				<Text weight="bold">{formatMessage(option.label)}</Text>
				<Text color={isSelected ? 'color.text.subtle' : 'color.text.subtlest'} size="small">
					{formatMessage(option.description)}
				</Text>
			</Flex>
		);
	};

	const onSelectMenuOpen = () => {
		if (typeof onMenuOpen === 'function') {
			onMenuOpen();
		}
	};

	const onSelectChange = (selection) => {
		if (typeof onChange === 'function') {
			onChange(selection.value);
		}
	};

	const spaceSelectionHeader = () => {
		if (isRbacEnabled) {
			return i18n.spaceAccessInclusionExclusionSelectionHeader;
		} else {
			return i18n.spaceSelectionHeader;
		}
	};

	return (
		<>
			<Inline xcss={spaceSelectionHeaderStyle} space="space.050" alignBlock="center">
				<Box xcss={spaceSelectionHeaderStyle}>
					{formatMessage(spaceSelectionHeader(), {
						asterisk: (chunks: React.ReactNode[]) => <Box xcss={asteriskStyle}>{chunks}</Box>,
					})}

					<Tooltip
						content={
							isRbacEnabled
								? formatMessage(i18n.archivedSpacesTooltipBulkAccess)
								: formatMessage(i18n.archivedSpacesTooltip)
						}
						position="right"
					>
						{(tooltipProps) => (
							<IconButton
								icon={(iconProps) => <StatusInformationIcon {...iconProps} size="small" />}
								appearance="subtle"
								label={formatMessage(i18n.archivedSpacesTooltipIconLabel)}
								spacing="compact"
								shape="circle"
								{...tooltipProps}
							/>
						)}
					</Tooltip>
				</Box>
			</Inline>
			{isRbacEnabled ? (
				<Box xcss={spaceSelectionSelectStyle}>
					<Select
						aria-label={formatMessage(i18n.spaceMenuTrigger)}
						inputId="bulk-access-space-selection"
						testId="bulk-access-space-selection-select"
						placeholder={formatMessage(i18n.spaceMenuTrigger)}
						options={spaceSelectionOptions}
						formatOptionLabel={formatOptionLabel}
						onChange={onSelectChange}
						onMenuOpen={onSelectMenuOpen}
						menuPosition="fixed"
						maxMenuHeight={320}
						scrollBehavior="outside"
						defaultValue={spaceSelectionOptions[0]}
						value={spaceSelectionOptions.find((option) => option.value === spaceSelection)}
						getOptionLabel={getOptionLabel}
						noOptionsMessage={() => formatMessage(i18n.spaceMenuNoOptions)}
					/>
				</Box>
			) : (
				<Box xcss={spaceSelectionRadioButtonStyle}>
					<Radio
						value={SpaceSelection.AllIncludingPersonal}
						onChange={onRadioChange}
						isChecked={spaceSelection === SpaceSelection.AllIncludingPersonal}
						name="space-selection"
						label={formatMessage(i18n.spaceSelectionOne)}
					/>
					<Radio
						value={SpaceSelection.AllExcludingPersonal}
						onChange={onRadioChange}
						isChecked={spaceSelection === SpaceSelection.AllExcludingPersonal}
						name="space-selection"
						label={formatMessage(i18n.spaceSelectionTwo)}
					/>
					<Radio
						value={SpaceSelection.OnlyPersonal}
						onChange={onRadioChange}
						isChecked={spaceSelection === SpaceSelection.OnlyPersonal}
						name="space-selection"
						label={formatMessage(i18n.spaceSelectionFour)}
					/>
				</Box>
			)}
		</>
	);
};
